import { ChangeEvent } from 'react'
import { PhoneInputProps } from 'react-phone-input-2'
import { GroupBase, OptionsOrGroups } from 'react-select'
import { AsyncAdditionalProps } from 'react-select/dist/declarations/src/useAsync'
import { CreatableAdditionalProps } from 'react-select/dist/declarations/src/useCreatable'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'

import { InputSize } from 'Components/_theme/Input/Input.types'
import { TextareaSize } from 'Components/_theme/Textarea/Textarea.types'
import { SelectOption, SelectProps } from 'Components/Select/Select.types'
import { SelectAsyncProps } from 'Components/SelectAsync/SelectAsync.types'
import { SelectCreateableProps } from 'Components/SelectCreateable/SelectCreateable.types'

export enum FieldTypeEnum {
  'text' = 'text',
  'password' = 'password',
  'email' = 'email',
  'textarea' = 'textarea',
  'textareaAutosize' = 'textareaAutosize',
  'checkbox' = 'checkbox',
  'radioButton' = 'radioButton',
  'phoneNumber' = 'phoneNumber',
  'select' = 'select',
  'selectCreatable' = 'selectCreatable',
  'selectAsync' = 'selectAsync',
  'datepicker' = 'datepicker',
  'number' = 'number',
}

type FieldWithoutComplexTypes = Exclude<
  FieldTypeEnum,
  | FieldTypeEnum.text
  | FieldTypeEnum.password
  | FieldTypeEnum.email
  | FieldTypeEnum.phoneNumber
  | FieldTypeEnum.select
  | FieldTypeEnum.selectCreatable
  | FieldTypeEnum.selectAsync
  | FieldTypeEnum.datepicker
  | FieldTypeEnum.textarea
  | FieldTypeEnum.textareaAutosize
  | FieldTypeEnum.number
>

type FieldPropsTypeGeneric = {
  type: `${FieldWithoutComplexTypes}`
}

type FieldPropsTypeWithPhoneInput = {
  type: `${FieldTypeEnum.phoneNumber}`
  phoneInputProps: PhoneInputProps
}

export type FieldPropsTypeWithSelect<
  Option extends SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = {
  type: `${FieldTypeEnum.select}`
  selectProps: SelectProps<Option, IsMulti, Group>
}

export type FieldPropsTypeWithSelectCreateable<
  Option extends SelectOption,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = {
  type: `${FieldTypeEnum.selectCreatable}`
  selectProps: SelectCreateableProps<Option, IsMulti, Group> &
    CreatableAdditionalProps<Option, Group>
}

export type FieldPropsTypeWithSelectAsync<
  Option extends SelectOption,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = {
  type: `${FieldTypeEnum.selectAsync}`
  selectProps: SelectAsyncProps<Option, IsMulti, Group> &
    AsyncAdditionalProps<Option, Group> & {
      lastOptions: OptionsOrGroups<Option, Group> | undefined
    }
}

export type FieldPropsWithDatepicker = {
  type: `${FieldTypeEnum.datepicker}`
  datepickerProps?: {
    showClearButton?: boolean
    min?: string | number | undefined
  }
}

export type FieldPropsTypeWithInput = {
  type:
    | `${FieldTypeEnum.text}`
    | `${FieldTypeEnum.email}`
    | `${FieldTypeEnum.password}`
    | `${FieldTypeEnum.number}`
  inputProps?: {
    contentAfter?: string | React.ReactElement
    contentBefore?: string | React.ReactElement
    iconLeft?: IconDefinition
    iconRight?: IconDefinition
    isClearable?: boolean
    size?: `${InputSize}`
    max?: string | number
    min?: string | number
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void
    allowUnderscoreCharacter?: boolean
  }
}

export type FieldPropsTypeWithTextarea = {
  type: `${FieldTypeEnum.textarea}` | `${FieldTypeEnum.textareaAutosize}`
  textareaProps?: {
    maxLength?: number
    showCounter?: boolean
    size?: TextareaSize
    isResizable?: boolean
  }
}

export type FieldCommon = {
  name: string
  containerClassName?: string
  feedbackClassName?: string
  labelClassName?: string
  inputClassName?: string
  label?: string
  placeholder?: string
  value?: string
  shouldShowErrorMessage?: boolean
  shouldTranslateLabel?: boolean
  markAsRequired?: boolean
  disabled?: boolean
  feedbackText?: string
  feedbackTranslationValues?: Record<string, string | number>
  e2eSelector?: string
  errorMessage?: string
}

export type FieldProps<
  Option extends SelectOption,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = FieldCommon &
  (
    | FieldPropsTypeGeneric
    | FieldPropsTypeWithInput
    | FieldPropsTypeWithPhoneInput
    | FieldPropsTypeWithSelect<Option, IsMulti, Group>
    | FieldPropsTypeWithSelectCreateable<Option, IsMulti, Group>
    | FieldPropsTypeWithSelectAsync<Option, IsMulti, Group>
    | FieldPropsWithDatepicker
    | FieldPropsTypeWithTextarea
  )

export type FieldCheckboxProps = {
  name: string
  hasError: boolean
  markAsRequired: boolean
  className?: string
  label?: string
  shouldTranslateLabel?: boolean
  e2eSelector?: string
}

export type FieldRadioButtonProps = {
  name: string
  hasError: boolean
  markAsRequired: boolean
  className?: string
  label?: string
  shouldTranslateLabel?: boolean
  e2eSelector?: string
}

export type FieldDatepickerProps = {
  name: string
  hasError: boolean
  label?: string
  datepickerProps?: {
    showClearButton?: boolean
    min?: string | number | undefined
  }
  inputClassName?: string
  e2eSelector?: string
}

export type FieldInputProps = {
  type: `${FieldTypeEnum}`
  name: string
  hasError: boolean
  placeholder?: string
  inputClassName?: string
  markAsRequired?: boolean
  inputProps?: {
    contentAfter?: string | React.ReactElement
    contentBefore?: string | React.ReactElement
    iconLeft?: IconDefinition
    iconRight?: IconDefinition
    isClearable?: boolean
    size?: `${InputSize}`
  }
  e2eSelector?: string
}

export type FieldPhoneInputProps = {
  name: string
  hasError: boolean
  phoneInputProps?: PhoneInputProps
  e2eSelector?: string
}

export type FieldSelectProps<
  Option extends SelectOption,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = {
  name: string
  hasError: boolean
  markAsRequired: boolean
  selectProps: SelectProps<Option, IsMulti, Group>
  placeholder?: string
  e2eSelector?: string
}

export type FieldSelectCreatableProps<
  Option extends SelectOption,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Omit<FieldSelectProps<Option, IsMulti, Group>, 'selectProps'> & {
  selectProps: SelectCreateableProps<Option, IsMulti, Group> &
    CreatableAdditionalProps<Option, Group>
  placeholder?: string
  e2eSelector?: string
}

export type FieldSelectAsyncProps<
  Option extends SelectOption,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Omit<FieldSelectProps<Option, IsMulti, Group>, 'selectProps'> & {
  selectProps: SelectAsyncProps<Option, IsMulti, Group> &
    AsyncAdditionalProps<Option, Group> & {
      lastOptions: OptionsOrGroups<Option, Group> | undefined
    }
  placeholder?: string
  e2eSelector?: string
}

export type FieldTextareaProps = {
  type: `${FieldTypeEnum}`
  name: string
  hasError: boolean
  markAsRequired: boolean
  placeholder?: string
  inputClassName?: string
  textareaProps?: {
    maxLength?: number
    showCounter?: boolean
    size?: TextareaSize
    isResizable?: boolean
  }
  e2eSelector?: string
}

export type FieldTextareaAutosizeProps = FieldTextareaProps
